import { TUserReportStatusEnum } from '@infotech-soe/merps/types/Reports';
import { TDatabaseId } from '@infotech-soe/metaschema';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import UniqueId from '../../services/uid/UniqueId';

import {
  emptyAction,
  failureAction,
  pendingAction,
  successAction,
} from './actionHandler';

export const enum NotificationOptionType {
  VALUE = 'value',
}

export const enum NotificationIcons {
  PRINT = 'print',
  ERROR = 'alert',
}

export interface NotificationOption {
  label: string;
  value: TDatabaseId;
  type: NotificationOptionType;
  icon?: NotificationIcons;
  disabled?: boolean;
}

interface BaseNotification {
  id: TDatabaseId;
  label: string;
  disabled?: boolean;
  message?: string;
  kind: string;
}

export interface PrintNotification extends BaseNotification {
  queueId: UniqueId;
  status: TUserReportStatusEnum;
  kind: 'print';
  icon: NotificationIcons.PRINT;
}

export interface ErrorNotification extends BaseNotification {
  kind: 'error';
  icon: NotificationIcons.ERROR;
}

export type SingleNotification = PrintNotification | ErrorNotification;

export type NotificationsViewModel = Record<string, SingleNotification>;

const initialState: NotificationsViewModel = {};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    append(state, { payload }: PayloadAction<SingleNotification>) {
      state[payload.id] = payload;
    },
    received: emptyAction<Record<string, any>>(),
    subscribe: emptyAction(),
    click: pendingAction<NotificationsViewModel, TDatabaseId>(),
    clickSuccess: successAction<NotificationsViewModel, { id: TDatabaseId }>(
      (state, { payload: { id } }) => {
        delete state[id];
      }
    ),
    clickFailure: failureAction(),
    get: pendingAction(),
    getSuccess: successAction<
      NotificationsViewModel,
      { notifications: NotificationsViewModel }
    >((state, { payload: { notifications } }) => {
      for (const notification of Object.values(notifications)) {
        state[notification.id] = notification;
      }
    }),
    getFailure: failureAction(),
    delete: pendingAction<NotificationsViewModel, { id: TDatabaseId }>(),
    deleteSuccess: successAction<NotificationsViewModel, { id: TDatabaseId }>(
      (state, { payload: { id } }) => {
        delete state[id];
      }
    ),
    deleteFailure: failureAction(),
  },
});

export const notificationsReducer = notificationsSlice.reducer;

export default notificationsSlice.actions;
